import React, { useState, useEffect } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import PanelHeader       from "./PanelHeader";
import AssociateCard     from "./AssociateCard";
import AllocationComparisonCard from "./AllocationComparisonCard";
import SkillsSection     from "./SkillsSection";
import ApprovalTimeline  from "./ApprovalTimeline";
import CommentHistory    from "./CommentHistory";
import ActionFooter      from "./ActionFooter";
import { getAllocationRequestById } from "../../../../../../api/approvals";

const SectionLabel = ({ children, t }) => (
  <Typography sx={{
    fontFamily: "'DM Mono', monospace", fontSize: "8px",
    letterSpacing: "0.18em", textTransform: "uppercase",
    color: t.text3, mb: "8px", mt: "16px",
    borderTop: `1px solid ${t.border}`, paddingTop: "12px",
  }}>
    {children}
  </Typography>
);

// ── Main panel ────────────────────────────────────────────────────────────────
const ReviewPanel = ({ request, onClose, onActionDone }) => {
  const theme = useTheme();
  const t     = theme.custom;
  const isDark = theme.palette.mode === "dark";

  // Navigation stack — starts with the original request, grows when user
  // clicks a parent request code. Back button pops the stack.
  const [navStack, setNavStack] = useState([]);

  // The "current" shallow stub being viewed — top of stack or the original prop
  const currentStub = navStack.length > 0 ? navStack[navStack.length - 1] : request;

  const [detail,  setDetail]  = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [activeBottomTab, setActiveBottomTab] = useState("timeline");

  // Reset stack whenever the root request prop changes (new card opened)
  useEffect(() => {
    setNavStack([]);
    setActiveBottomTab("timeline");
  }, [request?.requestId]);

  // Fetch full detail whenever the current stub changes
  useEffect(() => {
    if (!currentStub?.requestId) return;

    let cancelled = false;
    setDetail(null);
    setError(null);
    setLoading(true);

    getAllocationRequestById(currentStub.requestId)
      .then((data) => { if (!cancelled) setDetail(data); })
      .catch(() => { if (!cancelled) setError("Failed to load request details."); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [currentStub?.requestId]);

  if (!request) return null;

  const r = detail ?? currentStub;
  const isNavigated = navStack.length > 0;

  const handleNavigateToParent = () => {
    if (!detail?.parentRequestId) return;
    setNavStack((prev) => [...prev, {
      requestId:    detail.parentRequestId,
      requestCode:  detail.parentRequestCode,
      associateName: r.associateName,
      roleTitle:    r.roleTitle,
      status:       detail.parentRequestStatus,
    }]);
  };

  const handleNavigateToRequest = (target) => {
    if (!target?.requestId || target.requestId === currentStub?.requestId) return;

    if (target.requestId === request?.requestId) {
      setNavStack([]);
      return;
    }

    const stackIdx = navStack.findIndex((s) => s.requestId === target.requestId);
    if (stackIdx !== -1) {
      setNavStack((prev) => prev.slice(0, stackIdx + 1));
      return;
    }

    setNavStack((prev) => [...prev, {
      requestId:    target.requestId,
      requestCode:  target.requestCode,
      associateName: r.associateName,
      roleTitle:    r.roleTitle,
      status:       target.requestStatus,
    }]);
  };

  const handleBack = () => {
    setNavStack((prev) => prev.slice(0, -1));
  };

  return (
    <>
      {/* Backdrop */}
      <Box onClick={onClose} sx={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 299 }} />

      {/* Side panel */}
      <Box sx={{
        position: "fixed", top: "52px", right: 0, bottom: 0,
        width: "420px", zIndex: 300,
        background: t.surface,
        borderLeft: `1px solid ${t.border2}`,
        boxShadow: "-8px 0 32px rgba(0,0,0,0.2)",
        display: "flex", flexDirection: "column",
        overflow: "hidden",
      }}>

        {/* Back breadcrumb — shown when navigated into a parent or child */}
        {isNavigated && (
          <Box
            onClick={handleBack}
            sx={{
              display: "flex", alignItems: "center", gap: "6px",
              padding: "7px 20px",
              borderBottom: `1px solid ${t.border}`,
              cursor: "pointer",
              background: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
              "&:hover": { background: t.surface2 },
            }}
          >
            <Typography sx={{ fontSize: "13px", color: t.text3, lineHeight: 1 }}>←</Typography>
            <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", color: t.text3, letterSpacing: "0.06em" }}>
              Back to {navStack.length > 1 ? navStack[navStack.length - 2].requestCode : request.requestCode}
            </Typography>
          </Box>
        )}

        <PanelHeader request={r} onClose={onClose} />

        {/* Scrollable body */}
        <Box sx={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>

          {loading && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pt: "60px", gap: "12px" }}>
              <CircularProgress size={18} sx={{ color: t.accent }} />
              <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: t.text3, letterSpacing: "0.1em" }}>
                Loading details…
              </Typography>
            </Box>
          )}

          {!loading && error && (
            <Box sx={{ mt: "20px", padding: "10px 14px", background: "rgba(200,64,64,0.07)", border: "1px solid rgba(200,64,64,0.25)", borderRadius: "4px" }}>
              <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "11px", color: "#c84040" }}>
                {error}
              </Typography>
            </Box>
          )}

          {!loading && !error && detail && (
            <>
              <AssociateCard request={r} />

              <SectionLabel t={t}>Allocation</SectionLabel>
              <AllocationComparisonCard
                detail={detail}
                t={t}
                onNavigateToParent={handleNavigateToParent}
              />

              <SectionLabel t={t}>Skills Deployed on This Position</SectionLabel>
              <SkillsSection
                deployedSkills={r.deployedSkills ?? []}
                associateSkills={r.associateSkills ?? []}
              />

              {/* Section Tabs: Approval Timeline / Comment History */}
              <Box sx={{
                borderTop: `1px solid ${t.border}`,
                pt: "12px",
                mt: "16px",
                mb: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <Box
                    onClick={() => setActiveBottomTab("timeline")}
                    sx={{
                      cursor: "pointer",
                      px: "8px",
                      py: "4px",
                      borderRadius: "3px",
                      background: activeBottomTab === "timeline"
                        ? (isDark ? "rgba(40,179,180,0.15)" : "rgba(40,179,180,0.12)")
                        : "transparent",
                      border: `1px solid ${activeBottomTab === "timeline" ? (isDark ? "rgba(40,179,180,0.3)" : "rgba(40,179,180,0.35)") : "transparent"}`,
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      transition: "all 0.15s ease",
                      "&:hover": {
                        background: activeBottomTab === "timeline"
                          ? (isDark ? "rgba(40,179,180,0.2)" : "rgba(40,179,180,0.15)")
                          : t.surface2,
                      },
                    }}
                  >
                    <Typography sx={{
                      fontFamily: "'DM Mono', monospace",
                      fontSize: "8.5px",
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                      color: activeBottomTab === "timeline" ? t.accent : t.text3,
                      fontWeight: activeBottomTab === "timeline" ? 600 : 400,
                    }}>
                      Approval Timeline
                    </Typography>
                  </Box>

                  <Box
                    onClick={() => setActiveBottomTab("comments")}
                    sx={{
                      cursor: "pointer",
                      px: "8px",
                      py: "4px",
                      borderRadius: "3px",
                      background: activeBottomTab === "comments"
                        ? (isDark ? "rgba(40,179,180,0.15)" : "rgba(40,179,180,0.12)")
                        : "transparent",
                      border: `1px solid ${activeBottomTab === "comments" ? (isDark ? "rgba(40,179,180,0.3)" : "rgba(40,179,180,0.35)") : "transparent"}`,
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      transition: "all 0.15s ease",
                      "&:hover": {
                        background: activeBottomTab === "comments"
                          ? (isDark ? "rgba(40,179,180,0.2)" : "rgba(40,179,180,0.15)")
                          : t.surface2,
                      },
                    }}
                  >
                    <Typography sx={{
                      fontFamily: "'DM Mono', monospace",
                      fontSize: "8.5px",
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                      color: activeBottomTab === "comments" ? t.accent : t.text3,
                      fontWeight: activeBottomTab === "comments" ? 600 : 400,
                    }}>
                      Comment History
                    </Typography>
                    {r.commentHistory?.length > 0 && (
                      <Box sx={{
                        fontFamily: "'DM Mono', monospace",
                        fontSize: "8px",
                        fontWeight: 700,
                        px: "5px",
                        py: "1px",
                        borderRadius: "10px",
                        background: activeBottomTab === "comments" ? t.accent : t.surface3,
                        color: activeBottomTab === "comments" ? (isDark ? "#000" : "#fff") : t.text3,
                      }}>
                        {r.commentHistory.length}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>

              {/* Tab Content */}
              {activeBottomTab === "timeline" ? (
                <ApprovalTimeline request={r} />
              ) : (
                <CommentHistory
                  history={r.commentHistory}
                  onNavigateToParent={handleNavigateToParent}
                  parentRequestCode={detail?.parentRequestCode}
                  onNavigateToRequest={handleNavigateToRequest}
                />
              )}
            </>
          )}
        </Box>

        {/* Footer — hide approve/reject when viewing a historical parent */}
        <ActionFooter
          request={r}
          onClose={onClose}
          onActionDone={onActionDone}
          disabled={isNavigated}
        />
      </Box>
    </>
  );
};

export default ReviewPanel;